import {Info, Shuffle} from '@mui/icons-material';
import {Box, Button, Stack} from '@mui/material';
import {randomArray} from '@coolcolduk/util';
import {useCallback, useState} from 'react';
import {Base64ObjectInput, ExamModule, Question, QuestionStatus} from '@cp/model';
import DropdownDifficulty from '../../atoms/dropdownDifficulty/DropdownDifficulty';
import DropdownOptions from '../../atoms/dropdownOptions/DropdownOptions';
import TextWithRenderer from '../../atoms/textWithRenderer/TextWithRenderer';
import BondAnswerOptionListWithRemove from '../../bonds/bondAnswerOptionListWithRemove/BondAnswerOptionListWithRemove';
import BondGraphJsonRenderer from '../../bonds/bondGraphJsonRenderer/BondGraphJsonRenderer';
import BondImageUpload from '../../bonds/bondImageUpload/BondImageUpload';
import BondModuleTopicDropdown from '../../bonds/bondModuleTopicDropdown/BondModuleTopicDropdown';
import {QuestionFormData} from '../../types/QuestionFormData';
import StatusNoteDialog from '../StatusNoteDialog/StatusNoteDialog';
import QuestionEditStatusSection from './QuestionEditStatusSection';

export interface QuestionEditFormProps {
  formData: QuestionFormData;
  onFormDataChange: (data: QuestionFormData) => void;
  question?: Question | null; // Optional question for display purposes (e.g., imageUrl)
  examModules?: ExamModule[];
  modulesLoading?: boolean;
  isEditing?: boolean; // Whether we're editing an existing question
}

/**
 * Reusable question form component for creating and editing questions
 * This is a controlled component - all state is managed by the parent
 */
export default function QuestionEditForm({
  formData,
  onFormDataChange,
  question,
  examModules = [],
  modulesLoading = false,
  isEditing = false,
}: QuestionEditFormProps) {
  const [statusNoteDialogOpen, setStatusNoteDialogOpen] = useState(false);

  const handleInputChange = useCallback(
    (field: keyof QuestionFormData, value: any) => {
      onFormDataChange({...formData, [field]: value});
    },
    [formData, onFormDataChange],
  );

  const handleModuleChange = useCallback<(moduleId?: string) => void>(
    (moduleId) => {
      // Reset topic when module changes
      onFormDataChange({...formData, moduleId: moduleId || '', topic: ''});
    },
    [formData, onFormDataChange],
  );

  const handleStatusChange = useCallback(
    (newStatus: QuestionStatus) => {
      onFormDataChange({...formData, status: newStatus});
    },
    [formData, onFormDataChange],
  );

  const handleImageChange = useCallback(
    (image: Base64ObjectInput | null) => {
      onFormDataChange({...formData, image});
    },
    [formData, onFormDataChange],
  );

  const handleStatusNoteChange = useCallback(
    (note: string) => {
      onFormDataChange({...formData, statusNote: note});
    },
    [formData, onFormDataChange],
  );

  const handleRandomizeOptions = useCallback(() => {
    const randomizedOptions = randomArray([...formData.options]);
    onFormDataChange({...formData, options: randomizedOptions});
  }, [formData, onFormDataChange]);

  return (
    <Box>
      <Stack spacing={3}>
        <BondModuleTopicDropdown
          moduleId={formData.moduleId}
          topic={formData.topic}
          examModules={examModules}
          modulesLoading={modulesLoading}
          disabled={isEditing}
          moduleRequired
          topicRequired
          onModuleChange={handleModuleChange}
          onTopicChange={(topic) => handleInputChange('topic', topic)}
        />

        <DropdownDifficulty
          difficulty={formData.difficulty}
          onChange={(difficulty) => handleInputChange('difficulty', difficulty)}
        />

        <TextWithRenderer
          fullWidth
          label="Question Text"
          multiline
          rows={10}
          value={formData.questionText}
          onChange={useCallback(
            (e: React.ChangeEvent<HTMLTextAreaElement>) => handleInputChange('questionText', e.target.value),
            [handleInputChange],
          )}
          tooltipRender
        />

        <BondImageUpload
          currentImageUrl={question?.imageUrl || null}
          onImageChange={handleImageChange}
          label="Question Image (Optional)"
        />

        <BondGraphJsonRenderer
          graphJson={formData.graphJson}
          onGraphJsonChange={(value) => handleInputChange('graphJson', value)}
        />

        <TextWithRenderer
          fullWidth
          label="Hint (Optional)"
          multiline
          rows={2}
          value={formData.hint}
          onChange={useCallback(
            (e: React.ChangeEvent<HTMLTextAreaElement>) => handleInputChange('hint', e.target.value),
            [handleInputChange],
          )}
          tooltipRender
        />

        <BondAnswerOptionListWithRemove
          options={formData.options}
          onOptionChange={(options) => handleInputChange('options', options)}
        />

        <Button
          variant="outlined"
          startIcon={<Shuffle />}
          onClick={handleRandomizeOptions}
          disabled={formData.options.length < 2}
          sx={{alignSelf: 'flex-start'}}
        >
          Randomize Options
        </Button>

        <DropdownOptions
          options={formData.options}
          value={formData.answer}
          onChange={(value) => handleInputChange('answer', value)}
        />

        <TextWithRenderer
          fullWidth
          label="Explanation"
          multiline
          rows={3}
          value={formData.explanation}
          onChange={useCallback(
            (e: React.ChangeEvent<HTMLTextAreaElement>) => handleInputChange('explanation', e.target.value),
            [handleInputChange],
          )}
          helperText="This will be shown to students after they answer the question"
          tooltipRender
        />

        <QuestionEditStatusSection
          status={formData.status}
          statusNote={formData.statusNote || ''}
          flagLoading={false}
          hasQuestion={!!question}
          onStatusChange={handleStatusChange}
          onStatusNoteChange={handleStatusNoteChange}
        />

        {formData.statusNote && (
          <Button
            variant="outlined"
            startIcon={<Info />}
            onClick={() => setStatusNoteDialogOpen(true)}
            sx={{alignSelf: 'flex-start'}}
          >
            View Status Note
          </Button>
        )}
      </Stack>

      <StatusNoteDialog
        open={statusNoteDialogOpen}
        onClose={() => setStatusNoteDialogOpen(false)}
        statusNote={formData.statusNote || null}
        title="Status Note"
      />
    </Box>
  );
}
